// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BoostTokens {
  balance: number;
  lifetime_earned: number;
  reactions_given_this_week: number;
  reactions_received_this_week: number;
}

export const useBoostTokens = (profileId?: string) => {
  const [tokens, setTokens] = useState<BoostTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTokens = useCallback(async () => {
    if (!profileId) {
      setIsLoading(false);
      return;
    }

    try {
      // Call RPC to get or create tokens
      const { data, error } = await supabase
        .rpc('get_or_create_boost_tokens', { p_profile_id: profileId });

      if (error) {
        console.error('Error fetching boost tokens:', error);
        return;
      }

      if (data) {
        setTokens({
          balance: data.balance || 0,
          lifetime_earned: data.lifetime_earned || 0,
          reactions_given_this_week: data.reactions_given_this_week || 0,
          reactions_received_this_week: data.reactions_received_this_week || 0,
        });
      }
    } catch (err) {
      console.error('Error in fetchTokens:', err);
    } finally {
      setIsLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const boostPost = useCallback(async (postId: string): Promise<boolean> => {
    if (!profileId) {
      toast.error('You must be logged in to boost posts');
      return false;
    }

    if (!tokens || tokens.balance < 1) {
      toast.error('You need at least 1 boost token to boost a post');
      return false;
    }

    try {
      const { data, error } = await supabase
        .rpc('boost_post', { p_profile_id: profileId, p_post_id: postId });

      if (error) {
        console.error('Error boosting post:', error);
        toast.error('Failed to boost post');
        return false;
      }

      const result = data as { success: boolean; error?: string; new_balance?: number };
      
      if (result.success) {
        toast.success('⚡ Post boosted for 24 hours!');
        setTokens(prev => prev ? { ...prev, balance: result.new_balance || prev.balance - 1 } : null);
        return true;
      } else {
        toast.error(result.error || 'Failed to boost post');
        return false;
      }
    } catch (err) {
      console.error('Error in boostPost:', err);
      toast.error('Failed to boost post');
      return false;
    }
  }, [profileId, tokens]);

  // Calculate progress toward next token
  const reactionsToNextGivingToken = tokens ? 10 - (tokens.reactions_given_this_week % 10) : 10;
  const reactionsToNextReceivingToken = tokens ? 10 - (tokens.reactions_received_this_week % 10) : 10;

  return {
    tokens,
    isLoading,
    boostPost,
    refetch: fetchTokens,
    reactionsToNextGivingToken,
    reactionsToNextReceivingToken,
  };
};
